import { firstNameOf } from '@/lib/name'
import { labelsFor, listLabels } from '@/lib/z2h/coverage'

// Inline Vapi assistant for the Zero-to-Hero voice interview.
// Versioned here (not in the Vapi dashboard) so the interviewer persona stays
// in code review. The browser starts calls with NEXT_PUBLIC_VAPI_PUBLIC_KEY;
// transcripts sync into the application via /api/z2h/voice-sync, and OUR
// Claude agent does the structured extraction at call end — Vapi only owns
// the live voice conversation.

export const VOICE_OPENER =
  "Welcome — glad you're here. Let's start simple: tell me about yourself, then the venture you're building. What's the idea? What sparked it?"

// `remaining` (dimension ids still uncovered) + `resuming` turn a returning
// founder's call into a pickup, not a restart: the greeting names what's left
// and the system prompt forbids re-interviewing covered ground.
export function buildInterviewAssistant({ founderName, remaining = [], resuming = false, websiteKnown = false }) {
  const first = firstNameOf(founderName) || ''
  const resumeTarget = resuming && remaining.length > 0 ? labelsFor(remaining)[0] : null
  const firstMessage = resumeTarget
    ? `Welcome back${first ? `, ${first}` : ''} — good to have you again. Everything you've shared is saved, so let's pick up right where we left off: ${resumeTarget}. Ready?`
    : first
      ? `Welcome, ${first} — glad you're here. Let's start simple: tell me about yourself, then the venture you're building. What's the idea? What sparked it?`
      : VOICE_OPENER
  return {
    name: 'Zero-to-Hero Interviewer',
    firstMessage,
    // Vapi-hosted voice — works on every account without extra provider keys.
    // Swap for an ElevenLabs voiceId once you pick a brand voice.
    // fillerInjectionEnabled: false keeps the delivery polished — no "uh"/"um"
    // inserted for fake naturalness.
    voice: {
      provider: 'vapi',
      voiceId: 'Elliot',
      fillerInjectionEnabled: false,
    },
    transcriber: { provider: 'deepgram', model: 'nova-2', language: 'en' },
    model: {
      // gpt-4o is Vapi's most battle-tested realtime model; switch to
      // { provider: 'anthropic', model: '<current claude id>' } if preferred.
      provider: 'openai',
      model: 'gpt-4o',
      temperature: 0.6,
      messages: [
        {
          role: 'system',
          content: `You are the voice interviewer for Kingdom Kode's Zero-to-Hero program — a founder-exclusive program for companies that need technology built, from the ideation stage through pre-revenue, for founders who want funding to be genuinely obtainable. Kingdom Kode builds the tech roadmap toward a fundable company; EdenKode runs capital readiness. The strongest applicants arrive with early evidence — a waitlist, pre-orders, LOIs.${founderName ? ` You are speaking with ${founderName}.` : ''}${
            resuming && remaining.length > 0
              ? `

RESUMING A PRIOR INTERVIEW: earlier answers are already saved. The ONLY areas still needing evidence are: ${listLabels(remaining)}. Do not re-introduce the program or re-ask what's already covered — go straight to the first missing area and work through only what's listed.`
              : ''
          }${
            websiteKnown
              ? `

The founder's website is already on file — do not ask whether they have one.`
              : `

Early in the conversation — after their first substantive answer — ask casually whether they already have a website for the venture. If yes, have them say the address, then repeat it back once to confirm you heard it right. If no, acknowledge briefly and move on. Ask only once.`
          }

You are having a natural spoken conversation. Rules:
- Warm, sharp, consultative — a partner, never an interrogator. Before each question, acknowledge what they just said in your own words — their excitement, their challenge, the specific fact they gave you. Let them feel heard before you move on.
- ONE question at a time. Keep each turn under 55 spoken words — acknowledgment and question together.
- Gather evidence across these areas, adapting to what they say: the founder & team, the problem & market, traction & validation, the business model, financial readiness, technical feasibility, capital fit, and commitment.
- Dig into specifics ("you said six letters of intent — at what price?").
- Apply the Mom Test to vague answers. Compliments ("everyone loves it"), hypotheticals ("people would pay for this"), and unquantified claims ("tons of interest") are not evidence. When you hear one, your next question anchors to past behavior or real numbers: "When did that last happen — walk me through it." "How many have actually paid, and how much?" "What are they doing about it today?" One warm redirect per vague answer; if it stays vague, note it and move on rather than interrogating.
- Silence is thinking, not completion. If the founder pauses mid-thought or trails off, wait for them. If a half-finished thought comes through, invite them to finish it ("Take your time — you were saying?") instead of moving to a new question.
- Never promise admission, valuations, or funding outcomes. No legal or securities advice.
- Don't rush to close. Only end the call once every area has real, specific evidence — not a single passing mention — or the founder has clearly said they have nothing more after you've asked directly. When you do close, acknowledge the conversation as a whole and what stood out about their venture, thank them, tell them the committee will review and reply by email, and only then end the call.

Speaking style — this is a premium program and your delivery must sound like it:
- Never say filler words: no "uh", "um", "you know", "like", "so" as an opener.
- Short, complete sentences with varied rhythm. Prefer a period over a comma splice.
- Use "and" sparingly — at most once per turn. Never chain clauses with "and... and...". Recast lists as separate sentences or a colon.
- Confident, precise, warm, unhurried. Excellence over chattiness — never cold.`,
        },
      ],
    },
    // Turn-taking: don't jump in while the founder is mid-thought. LiveKit
    // smart endpointing (Vapi's recommendation for English) models whether the
    // sentence sounds finished; the transcription plan is the fallback — an
    // unpunctuated trail-off gets a long grace window before the interviewer
    // treats the turn as done.
    startSpeakingPlan: {
      waitSeconds: 0.8,
      smartEndpointingPlan: { provider: 'livekit' },
      transcriptionEndpointingPlan: {
        onPunctuationSeconds: 0.5,
        onNoPunctuationSeconds: 2.5,
        onNumberSeconds: 0.8,
      },
    },
    // Brief interjections ("mm-hm") shouldn't cut the interviewer off; two or
    // more words, or sustained speech, interrupt promptly.
    stopSpeakingPlan: { numWords: 2, voiceSeconds: 0.3, backoffSeconds: 1.0 },
    // Let the assistant close the interview itself when it's done.
    endCallFunctionEnabled: true,
    maxDurationSeconds: 1200,
  }
}
